import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./FAQ.css";

const faqData = [
  {
    question: "What makes this game different from other card games?",
    answer:
      "It blends the depth and deck-building of trading card games with the positional strategy of minis-based war games, offering deeply interactive and competitive PvP gameplay in a rich fantasy world.",
  },
  {
    question: "How many players can play?",
    answer:
      "The core game is designed for 2-4 players, but team formats and free-for-all variants are being developed for future releases.",
  },
  {
    question: "How long does a match take?",
    answer:
      "Most games last between 15–45 minutes depending on player experience and deck complexity.",
  },
  {
    question: "How do I win?",
    answer:
      "The standard win condition is to eliminate your opponent's Seeker(s) or claim the Shard.",
  },
  {
    question: "Are there classes or factions?",
    answer:
      'No rigid classes. Any Seeker can use any card they meet the stat requirements for. This means no locked-in archetypes and no "correct" way to play a hero.',
  },
  {
    question: "Is the game pay-to-win?",
    answer:
      "No. It is a major goal of ours that every single card is accessible without having to open dozens of packs (in our view, packs are primarily for players who want to Draft or want a cosmetic variant). So while there are collectible elements, victory ultimately depends on your skill, positioning, and timing, not who has the biggest wallet.",
  },
  {
    question: "Will there be organized play or tournaments?",
    answer:
      "Yes. We're building both in-person and online competitive circuits, including seasonal leagues, ranked ladders, and a championship series.",
  },
  {
    question: "When will Seekers: Shards of Imriel be available?",
    answer:
      "We're launching via Kickstarter in a few months. Early backers will get exclusive promo cards (cosmetic) and will help shape the future of the game. If that sounds interesting to you, be sure to sign up for Early Access.",
  },
  {
    question: "Can I contact you?",
    answer:
      "Yes! I'm more than happy to chat, whether it's praise, feedback, or ideas and suggestions. Join our discord: https://discord.gg/PCFPHUpYgW",
  },
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="faq" className="faq-section" ref={ref}>
      <div className="container">
        <motion.div
          className="faq-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="faq-header" variants={itemVariants}>
            <h2>Frequently Asked Questions</h2>
          </motion.div>

          {/* FAQ Items */}
          <motion.div className="faq-grid" variants={itemVariants}>
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                variants={itemVariants}
                layout
              >
                <motion.button
                  className={`faq-question ${
                    openIndex === index ? "active" : ""
                  }`}
                  onClick={() => toggleQuestion(index)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="question-text">{faq.question}</span>
                  <motion.span
                    className="question-icon"
                    animate={{ rotate: openIndex === index ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    +
                  </motion.span>
                </motion.button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <div className="answer-content">
                        <p>{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>


        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
